import { XIcon } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { useCallback, useLayoutEffect, useRef, useState, type ComponentProps, type RefObject } from 'react';
import { focusConnected, restoreFocus } from '../lib/focus';
import { DialogLayoutContext } from './dialog-layout';
import { cn } from '../lib/utils';
import { useUiStrings } from '../strings';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

export function DialogOverlay({ className, ...props }: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
}

export interface DialogContentProps extends ComponentProps<typeof DialogPrimitive.Content> {
  showCloseButton?: boolean;
  closeDisabled?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Header/footer stay outside the sole scrolling DialogBody. Existing layouts remain compatible. */
  framed?: boolean;
  /** Shared reserved flow footprint. Compact confirmations keep their natural size. */
  bodySize?: 'default' | 'compact';
  initialFocusRef?: RefObject<HTMLElement | null>;
  returnFocusRef?: RefObject<HTMLElement | null>;
  fallbackFocusRef?: RefObject<HTMLElement | null>;
}

const sizes = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

export function DialogContent({
  className,
  children,
  showCloseButton = true,
  closeDisabled = false,
  size = 'md',
  framed = false,
  bodySize,
  initialFocusRef,
  returnFocusRef,
  fallbackFocusRef,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onEscapeKeyDown,
  ref: forwardedRef,
  ...props
}: DialogContentProps) {
  const strings = useUiStrings();
  const capturedOpener = useRef<HTMLElement | null>(null);
  const [content, setContent] = useState<HTMLDivElement | null>(null);
  const contentDomRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useCallback((node: HTMLDivElement | null) => {
    contentDomRef.current = node;
    setContent(node);
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [forwardedRef]);
  useLayoutEffect(() => {
    const element = contentDomRef.current;
    if (!element) return;
    const previousHeight = element.style.height;
    let locked = false;
    const reserve = () => {
      if (locked || bodySize || !element.querySelector('[data-modal-tabs]')) return;
      // The shared panel grid already includes every tab's initial intrinsic
      // height. Keep that opening size when asynchronous previews arrive;
      // DialogBody scrolls instead of moving the header/footer and modal centre.
      const height = element.offsetHeight;
      if (height <= 0) return;
      element.style.height = `min(${height}px, 80dvh)`;
      locked = true;
      observer.disconnect();
    };
    const observer = new MutationObserver(reserve);
    reserve();
    if (!locked) observer.observe(element, { childList: true, subtree: true });
    return () => { observer.disconnect(); element.style.height = previousHeight; };
  }, [content, bodySize]);
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={contentRef}
        data-slot="dialog-content"
        data-layout={framed ? 'framed' : 'content'}
        className={cn(
          'fixed top-1/2 left-1/2 z-50 max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-lg border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          sizes[size],
          framed ? 'flex min-h-0 max-h-[80dvh] flex-col overflow-hidden' : 'grid overflow-y-auto',
          bodySize === 'default' && 'h-dialog-flow',
          bodySize === 'compact' && 'h-dialog-flow-compact',
          className,
        )}
        {...props}
        onEscapeKeyDown={(event) => {
          // Radix handles Escape before the combobox's bubbling key handler.
          // Give an expanded chooser its first Escape without dismissing its dialog.
          const target = event.target;
          if (target instanceof HTMLElement && target.getAttribute('role') === 'combobox' && target.getAttribute('aria-expanded') === 'true') {
            event.preventDefault();
            return;
          }
          onEscapeKeyDown?.(event);
        }}
        onOpenAutoFocus={(event) => {
          const active=document.activeElement;
          capturedOpener.current=active instanceof HTMLElement && active !== document.body ? active : null;
          onOpenAutoFocus?.(event); focusConnected(event, initialFocusRef);
        }}
        onCloseAutoFocus={(event) => { onCloseAutoFocus?.(event); restoreFocus(event, returnFocusRef, fallbackFocusRef, capturedOpener); }}
      >
        <DialogLayoutContext.Provider value>{children}</DialogLayoutContext.Provider>
        {showCloseButton && (
          <DialogPrimitive.Close disabled={closeDisabled} className="absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none disabled:opacity-30 [&_svg]:size-4">
            <XIcon />
            <span className="sr-only">{strings.close}</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
  return <div data-slot="dialog-header" className={cn('flex shrink-0 flex-col gap-1.5 text-left', className)} {...props} />;
}

/** The only vertical scroll owner inside a framed dialog. */
export function DialogBody({ className, ...props }: ComponentProps<'div'>) {
  return <div data-slot="dialog-body" className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return <div data-slot="dialog-footer" className={cn('flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)} {...props} />;
}

export function DialogTitle({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title data-slot="dialog-title" className={cn('text-lg leading-none font-semibold', className)} {...props} />;
}

export function DialogDescription({ className, ...props }: ComponentProps<typeof DialogPrimitive.Description>) {
  return <DialogPrimitive.Description data-slot="dialog-description" className={cn('text-sm text-muted-foreground', className)} {...props} />;
}
